import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useBuildingService from '../../hooks/useBuildingService';
import useOrderService from '../../hooks/useOrderService';
import useClientService from '../../hooks/useClientService';
import useDocumentService from '../../hooks/useDocumentService';
import useInvoiceService from '../../hooks/useInvoiceService';
import useCertificationService from '../../hooks/useCertificationService';
import useDeliveryNoteService from '../../hooks/useDeliveryNoteService';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    Button,
    TextField,
    Grid,
    Autocomplete,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Paper,
    FormHelperText,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    Box,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import Accordion from '../../components/Accordion';
import InvoiceRetentionTable from '../../components/InvoiceRetentionTable';
import CreatePdf from '../../components/CreatePdf';
import UploadDocument from '../../components/UploadDocument';
import { formatDate, DatePickerComponent, handleApiError, itemsPerPage, IconBtn } from '../../components/Utils';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';

dayjs.extend(utc);

function EditBuilding() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();

    const { getBuilding, updateBuilding, disableBuilding, addBuildingItem, disableBuildingItem } = useBuildingService();
    const { getClients } = useClientService();
    const { getDocumentBlob } = useDocumentService();
    const { createInvoice, disableInvoice, getInvoices } = useInvoiceService();
    const { createCertification, disableCertification, getCertifications } = useCertificationService();
    const { disableDeliveryNoteFromOrder } = useOrderService();
    const { getDeliveryNotes } = useDeliveryNoteService();

    // --- General loading (building form only) ---
    const [loading, setLoading] = useState({ general: true, update: false });
    const [editError, setEditError] = useState(null);
    const [itemsError, setItemsError] = useState(null);

    const [client, setClient] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);

    const [items, setItems] = useState([]);
    const [budget, setBudget] = useState(0);

    const [status, setStatus] = useState('');
    const [retention, setRetention] = useState(null);
    const [vat, setVat] = useState(null);
    const [retentionDate, setRetentionDate] = useState(null);
    const [invoiceLimitDate, setInvoiceLimitDate] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(dayjs().startOf('day').local().format());

    const [errors, setErrors] = useState({
        vat: '',
        retention: '',
    });

    const [buildingForm, setBuildingForm] = useState({
        name: null,
        start_date: null,
        end_date: null,
        description: null,
        building_status: null,
        client_id: null
    });

    const [openItemModal, setOpenItemModal] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(false);
    const [typeDelete, setTypeDelete] = useState(false);

    const [certificationForm, setCertificationForm] = useState({});
    const [openCertModal, setOpenCertModal] = useState(false);
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [selectedCert, setSelectedCert] = useState(null);

    const [openInfoModal, setOpenInfoModal] = useState(false);
    const [infoContent, setInfoContent] = useState({ name: '', description: '' });

    const [itemForm, setItemForm] = useState({
        name: null,
        description: null,
        unit_price: null,
        quantity: null,
        unit: null,
    });
    const [itemFormErrors, setItemFormErrors] = useState({
        name: '',
        description: '',
        unit_price: '',
        quantity: '',
        unit: ''
    });

    // --- Certifications section state ---
    const [certifications, setCertifications] = useState([]);
    const [certificationsPage, setCertificationsPage] = useState(1);
    const [certificationsTotal, setCertificationsTotal] = useState(0);
    const [loadingCertifications, setLoadingCertifications] = useState(false);
    const [certificationsError, setCertificationsError] = useState(null);

    // --- Client invoices section state ---
    const [clientInvoices, setClientInvoices] = useState([]);
    const [clientInvoicesPage, setClientInvoicesPage] = useState(1);
    const [clientInvoicesTotal, setClientInvoicesTotal] = useState(0);
    const [loadingClientInvoices, setLoadingClientInvoices] = useState(false);
    const [clientTotalPriceSum, setClientTotalPriceSum] = useState(null);
    const [clientPaidAmountSum, setClientPaidAmountSum] = useState(null);
    const [clientRemainingAmountSum, setClientRemainingAmountSum] = useState(null);
    const [clientTotalRetention, setClientTotalRetention] = useState(null);
    const [clientPaidRetention, setClientPaidRetention] = useState(null);
    const [clientRemainingRetentionSum, setClientRemainingRetentionSum] = useState(null);

    // --- Provider invoices section state ---
    const [providerInvoices, setProviderInvoices] = useState([]);
    const [providerInvoicesPage, setProviderInvoicesPage] = useState(1);
    const [providerInvoicesTotal, setProviderInvoicesTotal] = useState(0);
    const [loadingProviderInvoices, setLoadingProviderInvoices] = useState(false);
    const [providerTotalPriceSum, setProviderTotalPriceSum] = useState(null);
    const [providerPaidAmountSum, setProviderPaidAmountSum] = useState(null);
    const [providerRemainingAmountSum, setProviderRemainingAmountSum] = useState(null);
    const [providerTotalRetention, setProviderTotalRetention] = useState(null);
    const [providerPaidRetention, setProviderPaidRetention] = useState(null);
    const [providerRemainingRetentionSum, setProviderRemainingRetentionSum] = useState(null);

    // --- Delivery notes section state ---
    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [deliveryNotesPage, setDeliveryNotesPage] = useState(1);
    const [deliveryNotesTotal, setDeliveryNotesTotal] = useState(0);
    const [loadingDeliveryNotes, setLoadingDeliveryNotes] = useState(false);

    // -------------------------------------------------------------------------
    // Fetches
    // -------------------------------------------------------------------------

    const fetchBuilding = useCallback(async () => {
        try {
            setLoading(prev => ({ ...prev, general: true }));
            const result = await getBuilding(id);
            const building = result.data.building;
            setBuildingForm(building);
            setBuildingForm(prevState => ({ ...prevState, building_id: id }));
            setSelectedClient(building.client);
            setClient(building.client);
            setStatus(building.building_status);
            setItems(building.items);
            setBudget(building.budget);
        } catch (error) {
            setEditError(handleApiError(error, 'Error obteniendo obra.'));
        } finally {
            setLoading(prev => ({ ...prev, general: false }));
        }
    }, [id]);

    const fetchClients = useCallback(async () => {
        try {
            const result = await getClients();
            setClients(result.data.items);
        } catch (error) {
            setEditError(handleApiError(error, 'Error al obtener clientes.'));
        }
    }, []);

    const fetchCertifications = useCallback(async () => {
        try {
            setLoadingCertifications(true);
            const result = await getCertifications({
                building_id: id,
                limit: itemsPerPage,
                offset: (certificationsPage - 1) * itemsPerPage,
                disabled: false,
            });
            setCertifications(result.data.items);
            setCertificationsTotal(result.data.total);
        } catch (error) {
            setCertificationsError(handleApiError(error, 'Error obteniendo certificaciones.'));
        } finally {
            setLoadingCertifications(false);
        }
    }, [id, certificationsPage]);

    const fetchClientInvoices = useCallback(async () => {
        try {
            setLoadingClientInvoices(true);
            const result = await getInvoices({
                building_id: id,
                only_client_invoices: true,
                limit: itemsPerPage,
                offset: (clientInvoicesPage - 1) * itemsPerPage,
                disabled: false,
            });
            setClientInvoices(result.data.items);
            setClientInvoicesTotal(result.data.total);
            setClientTotalPriceSum(result.data.total_price_sum);
            setClientPaidAmountSum(result.data.paid_amount_sum);
            setClientRemainingAmountSum(result.data.remaining_amount_sum);
            setClientTotalRetention(result.data.total_retentions);
            setClientPaidRetention(result.data.paid_retention);
            setClientRemainingRetentionSum(result.data.remaining_retention_sum);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo facturas de cliente.'), 'error');
        } finally {
            setLoadingClientInvoices(false);
        }
    }, [id, clientInvoicesPage]);

    const fetchProviderInvoices = useCallback(async () => {
        try {
            setLoadingProviderInvoices(true);
            const result = await getInvoices({
                building_id: id,
                only_provider_invoices: true,
                limit: itemsPerPage,
                offset: (providerInvoicesPage - 1) * itemsPerPage,
                disabled: false,
            });
            setProviderInvoices(result.data.items);
            setProviderInvoicesTotal(result.data.total);
            setProviderTotalPriceSum(result.data.total_price_sum);
            setProviderPaidAmountSum(result.data.paid_amount_sum);
            setProviderRemainingAmountSum(result.data.remaining_amount_sum);
            setProviderTotalRetention(result.data.total_retentions);
            setProviderPaidRetention(result.data.paid_retention);
            setProviderRemainingRetentionSum(result.data.remaining_retention_sum);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo facturas de proveedor.'), 'error');
        } finally {
            setLoadingProviderInvoices(false);
        }
    }, [id, providerInvoicesPage]);

    const fetchDeliveryNotes = useCallback(async () => {
        try {
            setLoadingDeliveryNotes(true);
            const result = await getDeliveryNotes({
                building_id: id,
                limit: itemsPerPage,
                offset: (deliveryNotesPage - 1) * itemsPerPage,
                disabled: false,
            });
            setDeliveryNotes(result.data.items);
            setDeliveryNotesTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo albaranes.'), 'error');
        } finally {
            setLoadingDeliveryNotes(false);
        }
    }, [id, deliveryNotesPage]);

    // -------------------------------------------------------------------------
    // Effects – one per section (fire on mount + page change)
    // -------------------------------------------------------------------------

    useEffect(() => {
        fetchBuilding();
        fetchClients();
    }, []);

    useEffect(() => {
        fetchCertifications();
    }, [certificationsPage]);

    useEffect(() => {
        fetchClientInvoices();
    }, [clientInvoicesPage]);

    useEffect(() => {
        fetchProviderInvoices();
    }, [providerInvoicesPage]);

    useEffect(() => {
        fetchDeliveryNotes();
    }, [deliveryNotesPage]);

    // -------------------------------------------------------------------------
    // Handlers – form / items
    // -------------------------------------------------------------------------

    const handleOpenItemModal = () => {
        setItemForm((prevState) => ({
            ...prevState,
            building_id: id,
        }));
        setOpenItemModal(true);
    };

    const handleCloseItemModal = () => {
        setOpenItemModal(false);
        setItemForm({ name: null, description: null, quantity: null, unit: null, unit_price: null });
        setItemFormErrors({ name: '', description: '', quantity: '', unit: '', unit_price: '' });
    };

    const handleItemChange = (event) => {
        const { name, value } = event.target;
        setItemForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        setItemFormErrors((prevState) => ({
            ...prevState,
            [name]: ''
        }));
    };

    const handleItemSubmit = async () => {
        try {
            const errors = validateItemForm();
            if (Object.keys(errors).length > 0) {
                setItemFormErrors(errors);
                return;
            }
            setItemsError(null);
            setLoading(prev => ({ ...prev, general: true }));
            const result = await addBuildingItem(itemForm);
            showSnackbar('Partida añadida correctamente', 'success');
            handleCloseItemModal();
            setItemForm((prevState) => ({
                ...prevState,
                name: null,
                description: null,
                quantity: null,
                unit: null,
                unit_price: null
            }));
            const building = result.data;
            setBudget(building.budget);
            setItems(building.items);
        } catch (error) {
            const msg = handleApiError(error, 'Error al añadir partida.');
            setItemsError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoading(prev => ({ ...prev, general: false }));
        }
    };

    const validateItemForm = () => {
        const errors = {};
        if (!itemForm.name) {
            errors.name = 'El nombre es requerido';
        }
        if (!itemForm.unit_price) {
            errors.unit_price = 'El precio unitario es requerido';
        } else {
            const unit_price = parseFloat(itemForm.unit_price);
            if (unit_price > 99000000) {
                errors.unit_price = "El precio unitario no puede ser mayor a 99 millones.";
            } else if (!Number.isInteger(unit_price * 100)) {
                errors.unit_price = "El precio unitario solo puede tener hasta 2 decimales.";
            }
            else if (unit_price <= 0) {
                errors.unit_price = 'El precio unitario debe ser mayor que 0';
            }
        }
        if (!itemForm.quantity) {
            errors.quantity = 'La cantidad es requerida';
        } else {
            const quantity = parseFloat(itemForm.quantity);
            if (quantity > 99000000) {
                errors.quantity = "La cantidad no puede ser mayor a 99 millones.";
            } else if (!Number.isInteger(quantity * 100)) {
                errors.quantity = "La cantidad solo puede tener hasta 2 decimales.";
            }
            else if (itemForm.quantity <= 0) {
                errors.quantity = 'La cantidad debe ser mayor que 0';
            }
        }
        if (!itemForm.unit) {
            errors.unit = 'La unidad es requerida';
        }
        return errors;
    };

    const handleClickOpenCert = () => {
        setOpenCertModal(true);
        setCertificationForm({
            building_id: id,
            description: null,
            certification_date: null,
            items: items.map(item => ({ item_id: item.id, percentage: null })),
        })
    };

    const handleCloseCert = () => {
        setOpenCertModal(false);
    };

    const handleClickOpenInvoice = (cert) => {
        setSelectedCert(cert);
        setOpenInvoiceModal(true);
    };

    const handleCloseInvoice = () => {
        setRetention(null);
        setRetentionDate(null);
        setOpenInvoiceModal(false);
    };

    const handleCreateCertification = async () => {
        try {
            handleCloseCert();
            setLoadingCertifications(true);
            setCertificationsError(null);
            const filteredCertificationForm = {
                ...certificationForm,
                items: certificationForm.items.filter(item => item.percentage)
            };
            await createCertification(filteredCertificationForm);
            showSnackbar('Certificacion creada correctamente', 'success');
            handleCloseItemModal();
            setCertificationForm({});
            fetchCertifications();
        } catch (error) {
            const msg = handleApiError(error, 'Error al crear certificación.');
            setCertificationsError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoadingCertifications(false);
        }
    };

    const handleCreateInvoice = async () => {
        try {
            setLoadingClientInvoices(true);
            await createInvoice({
                certification: selectedCert.id,
                retention: retention,
                vat: vat,
                invoice_limit_date: invoiceLimitDate,
                invoice_date: invoiceDate,
                retention_limit_date: retentionDate,
            });
            showSnackbar('Factura creada correctamente', 'success');
            handleCloseInvoice();
            fetchClientInvoices();
        } catch (error) {
            const msg = handleApiError(error, 'Error al crear factura.');
            showSnackbar(msg, 'error');
        } finally {
            setLoadingClientInvoices(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setBuildingForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleChangeInvoiceModal = (event) => {
        const { name, value } = event.target;
        if (name === 'vat') {
            setVat(value);
        } else if (name === 'retention') {
            setRetention(value)
        }

        const numericValue = Number(value);

        if (!isNaN(numericValue) && Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 100) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, [name]: 'El valor debe ser un número entero entre 0 y 100' }));
        }
    };

    const handleChangeCertification = (event) => {
        const { name, value } = event.target;
        setCertificationForm((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
        setBuildingForm((prevState) => ({
            ...prevState,
            building_status: value,
        }));
    };

    const handleDateChange = (name, newValue) => {
        const date = dayjs(newValue).hour(12).minute(0).second(0).utc().format('YYYY-MM-DD');

        if (name === 'retention_limit_date') {
            setRetentionDate(date);
        } else if (name === 'invoice_limit_date') {
            setInvoiceLimitDate(date);
        } else if (name === 'invoice_date') {
            setInvoiceDate(date);
        } else if (name === 'certification_date') {
            setCertificationForm((prevState) => ({
                ...prevState,
                certification_date: date,
            }));
        } else {
            setBuildingForm((prevState) => ({
                ...prevState,
                [name]: date,
            }));
        }
    };

    const handleClientChange = (event, newValue) => {
        setSelectedClient(newValue);
        setBuildingForm((prevState) => ({
            ...prevState,
            client_id: newValue ? newValue.id : '',
        }));
    };

    const validateForm = () => {
        if (!buildingForm.name) {
            return 'Por favor, añada un nombre';
        }
        if (buildingForm.name.length > 36) {
            return 'El nombre no debe exceder los 36 caracteres';
        }
        if (!buildingForm.client_id) {
            return 'Por favor, seleccione un cliente';
        }
        if (!buildingForm.building_status) {
            return 'Por favor, seleccione el estado de la obra';
        }
        if (buildingForm.retention) {
            const integerRegex = /^\d+$/;
            if (!integerRegex.test(buildingForm.retention.toString()) || buildingForm.retention < 0 || buildingForm.retention > 100) {
                return 'La retención debe ser un número entero comprendido entre el 0 y el 100';
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setEditError(null);
        const validationError = validateForm();
        if (validationError) {
            setEditError(validationError);
            return;
        }
        try {
            setLoading(prev => ({ ...prev, update: true }));
            const result = await updateBuilding(buildingForm);
            showSnackbar('Obra actualizada correctamente', 'success');
            const building = result.data;
            setBuildingForm(building);
            setBuildingForm(prevState => ({ ...prevState, building_id: id }));
            setSelectedClient(building.client);
            setClient(building.client);
            setStatus(building.building_status);
            setEditError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al actualizar obra.');
            setEditError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleInfoOpen = (content) => {
        setInfoContent(content);
        setOpenInfoModal(true);
    };

    const handleInfoClose = () => {
        setOpenInfoModal(false);
        setInfoContent({ name: '', description: '' });
    };

    const handleDeleteItem = async () => {
        try {
            if (typeDelete === '/buildings/disable') {
                await disableBuilding(deleteId);
            } else if (typeDelete === '/buildings/disable_item') {
                const result = await disableBuildingItem(deleteId);
                const building = result.data;
                setItems(building.items);
                setBudget(building.budget);
            } else if (typeDelete === '/certifications/disable') {
                await disableCertification(deleteId);
                fetchCertifications();
            } else if (typeDelete === '/invoices/disable') {
                await disableInvoice(deleteId);
                fetchClientInvoices();
                fetchProviderInvoices();
            } else if (typeDelete === '/orders/disable_dn') {
                await disableDeliveryNoteFromOrder({ delivery_note_id: deleteId });
                fetchDeliveryNotes();
            }

            showSnackbar('Eliminado correctamente', 'success');
            if (typeDelete === '/buildings/disable') {
                navigate(`/buildings/list`);
            }
            setOpenDeleteDialog(false);
        } catch (error) {
            const msg = handleApiError(error, 'Error al eliminar.');
            showSnackbar(msg, 'error');
        }
    };

    const handleOpenDeleteDialog = (itemId, type) => {
        setDeleteId(itemId);
        setTypeDelete(type);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleInputCertificationChange = (itemId, value) => {
        setCertificationForm((prevFormData) => {
            const updatedItem = prevFormData.items.map(item =>
                item.item_id === itemId ? { ...item, percentage: value } : item
            );
            return {
                ...prevFormData,
                items: updatedItem,
            };
        });
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setEditError(handleApiError(error, 'Error al descargar el documento.'));
        }
    };

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div style={{ padding: 20 }}>
            {loading?.general && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading?.general && (
                <div>
                    <Grid container spacing={3} style={{ position: 'fixed', top: 60, right: 10, zIndex: 1100, width: 'auto' }}>
                        <Grid item>
                            <Paper elevation={0} style={{ padding: '10px', textAlign: 'center', backgroundColor: '#fff' }}>
                                <Box mb={2}>
                                    <Typography variant="h5" component="h1" gutterBottom>
                                        {buildingForm.code} - {buildingForm.name}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={10} lg={8}>
                        {/* Building form accordion */}
                        <Accordion title={`${buildingForm.code} ${buildingForm.name}`}>
                            <form onSubmit={handleSubmit} style={{ padding: '10px' }}>
                                <Grid container spacing={2} style={{ padding: 8 }}>
                                    {/* Primera fila: Nombre, Presupuesto, Cliente y Estado */}
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Nombre"
                                            name="name"
                                            value={buildingForm.name || ''}
                                            onChange={handleChange}
                                            fullWidth
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!buildingForm.name }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            options={clients}
                                            getOptionLabel={(option) => option.name || ''}
                                            value={selectedClient || null}
                                            onChange={handleClientChange}
                                            renderInput={(params) => <TextField {...params} label="Cliente" margin="normal" />}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="status-label">Estado</InputLabel>
                                            <Select
                                                labelId="status-label"
                                                id="status"
                                                name="status"
                                                value={status}
                                                onChange={handleStatusChange}
                                                label="Estado"
                                            >
                                                <MenuItem value="not_started">NO EMPEZADA</MenuItem>
                                                <MenuItem value="in_course">EN CURSO</MenuItem>
                                                <MenuItem value="finished">TERMINADA</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Presupuesto"
                                            name="budget"
                                            value={budget.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€'}
                                            margin="normal"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                        />
                                    </Grid>

                                    {/* Segunda fila: Fecha inicio y Fecha final */}
                                    <Grid item xs={12} sm={3}>
                                        <DatePickerComponent
                                            label="Fecha inicio"
                                            value={buildingForm.start_date}
                                            onChange={(newValue) => handleDateChange('start_date', newValue)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <DatePickerComponent
                                            label="Fecha final"
                                            value={buildingForm.end_date}
                                            onChange={(newValue) => handleDateChange('end_date', newValue)}
                                        />
                                    </Grid>

                                    {/* Tercera fila: Descripción */}
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Descripción"
                                            name="description"
                                            value={buildingForm.description || ''}
                                            onChange={handleChange}
                                            multiline
                                            rows={4}
                                            fullWidth
                                            margin="normal"
                                            inputProps={{ maxLength: 255 }}
                                        />
                                    </Grid>

                                    {/* Cuarta fila: Botones */}
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            onClick={() => handleOpenDeleteDialog(id, '/buildings/disable')}
                                            fullWidth
                                            style={{ backgroundColor: 'red' }}>
                                            Eliminar
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="small"
                                            color="primary"
                                            disabled={loading?.update}
                                            fullWidth>
                                            {loading?.update ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Actualizar'
                                            )}
                                        </Button>
                                    </Grid>

                                    {/* Error */}
                                    {editError && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                                {editError}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </form>
                        </Accordion>

                        {/* Items / Partidas accordion */}
                        <Accordion title='Partidas de la obra'>
                            {buildingForm?.offer && (
                                <Grid item xs={12} md={3}>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', fontSize: '16px' }}>
                                        Enlace a la oferta
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', display: 'flex', flexDirection: 'column', ml: 2 }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <strong>Código:&nbsp;</strong>
                                            <Link to={`/offers/update/${buildingForm?.offer.id}`}>
                                                {buildingForm?.offer.code || '-'}
                                            </Link>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <strong>Nombre:&nbsp;</strong>
                                            {buildingForm?.offer.name || '-'}
                                        </div>
                                        <br />
                                    </Typography>
                                </Grid>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    onClick={handleOpenItemModal}
                                    sx={{ mb: 2 }}>
                                    Añadir Partida
                                </Button>
                                <p style={{ marginBottom: 30 }}><strong>Presupuesto: </strong>{budget.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€'}</p>
                            </div>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>Descripción</TableCell>
                                            <TableCell>Precio unitario</TableCell>
                                            <TableCell>Cantidad</TableCell>
                                            <TableCell>Importe total</TableCell>
                                            <TableCell>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.filter(item => !item.disabled).map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.name}</TableCell>
                                                <TableCell>
                                                    {item.description
                                                        ? (item.description.length > 50
                                                            ? item.description.substring(0, 50) + '...'
                                                            : item.description)
                                                        : ''}
                                                </TableCell>
                                                <TableCell>{item.unit_price ? item.unit_price.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : null}</TableCell>
                                                <TableCell>{item.quantity ? item.quantity.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null} {item.unit === 'l' ? 'LITROS' :
                                                    item.unit === 'kg' ? 'KGS' :
                                                        item.unit === 'uds' ? 'UNIDADES' :
                                                            item.unit === 'hour' ? 'HORAS' :
                                                                item.unit === 'm3' ? 'M3' :
                                                                    item.unit}
                                                </TableCell>
                                                <TableCell>
                                                    {item.unit_price && item.quantity
                                                        ? (item.unit_price * item.quantity).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€'
                                                        : null}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleInfoOpen({ name: item.name, description: item.description })}
                                                    >
                                                        Ver descripción
                                                    </Button>
                                                    <IconBtn
                                                        icon={<DeleteIcon />}
                                                        title="Eliminar"
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(item.id, '/buildings/disable_item')}
                                                        sx={{ ml: 2 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {itemsError && (
                                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                    {itemsError}
                                </Typography>
                            )}
                        </Accordion>

                        {/* Client info accordion */}
                        <Accordion title='Detalles del cliente'>
                            <Grid container spacing={3} style={{ marginBottom: 20 }}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Nombre:</Typography>
                                    <Typography variant="body1">{client.name} {client.surname}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Código cliente:</Typography>
                                    <Typography variant="body1">
                                        <Link to={`/clients/update/${client.id}`}>
                                            {client.code}
                                        </Link>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Correo:</Typography>
                                    <Typography variant="body1">{client.email}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Teléfono:</Typography>
                                    <Typography variant="body1">{client.telephone_number}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Documentación:</Typography>
                                    <Typography variant="body1">{client.documentation}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle1">Dirección:</Typography>
                                    <Typography variant="body1">{client.country} {client.town} {client.province} {client.cp} {client.address}</Typography>
                                </Grid>
                            </Grid>
                        </Accordion>

                        {/* Certifications accordion – paginated */}
                        <Accordion
                            title='Certificaciones'
                            paginate
                            totalItems={certificationsTotal}
                            currentPage={certificationsPage}
                            onPageChange={setCertificationsPage}
                        >
                            <Button variant="contained" color="primary" sx={{ mr: 2 }} onClick={handleClickOpenCert}>
                                Crear certificación
                            </Button>
                            {loadingCertifications ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Facturada</TableCell>
                                                <TableCell>Precio</TableCell>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Partes de la obra</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {certifications.filter(item => !item.disabled).map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Link to={`/certifications/update/${item.id}`}>
                                                            {item.code}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{item.invoiced ? "Sí" : "No"}</TableCell>
                                                    <TableCell>{item.price ? item.price.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : null}</TableCell>
                                                    <TableCell>{formatDate(item.certification_date)}</TableCell>
                                                    <TableCell>
                                                        {item.item_progress.map((product, idx) => (
                                                            <div key={idx}>{product.item.name} - {product.percentage}%</div>
                                                        ))}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="small"
                                                                disabled={item.invoiced}
                                                                onClick={() => handleClickOpenInvoice(item)}
                                                                sx={{ minHeight: 34 }}
                                                            >
                                                                Crear factura
                                                            </Button>
                                                            <CreatePdf
                                                                endpoint={`/certifications/${item.id}/generate-pdf`}
                                                            />
                                                            <IconBtn
                                                                icon={<DeleteIcon />}
                                                                title="Eliminar"
                                                                color="error"
                                                                onClick={() => handleOpenDeleteDialog(item.id, '/certifications/disable')}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {certificationsError && (
                                        <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                            {certificationsError}
                                        </Typography>
                                    )}
                                </TableContainer>
                            )}
                        </Accordion>

                        {/* Delivery notes accordion – paginated */}
                        <Accordion
                            title='Albaranes'
                            paginate
                            totalItems={deliveryNotesTotal}
                            currentPage={deliveryNotesPage}
                            onPageChange={setDeliveryNotesPage}
                        >
                            {loadingDeliveryNotes ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {deliveryNotes.filter(item => !item.disabled).map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Link to={`/delivery_notes/update/${item.id}`}>
                                                            {item.code}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{formatDate(item.dn_date)}</TableCell>
                                                    <TableCell>
                                                        {item.documents && item.documents.length > 0 && (
                                                            <IconBtn
                                                                icon={<DownloadIcon />}
                                                                title="Descargar"
                                                                onClick={() =>
                                                                    handleDownloadDocument(item.documents[0].id, item.documents[0].name)
                                                                }
                                                                sx={{ marginRight: 2 }}
                                                            />
                                                        )}
                                                        <UploadDocument
                                                            to={'delivery_note_id'}
                                                            id={item.id}
                                                            fetch={fetchDeliveryNotes}
                                                            showDisableDoc={item.documents && item.documents.length > 0}
                                                        >
                                                        </UploadDocument>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/orders/disable_dn')}
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Accordion>

                        {/* Client invoices – paginated, passed to InvoiceRetentionTable */}
                        <InvoiceRetentionTable
                            isInvoice={true}
                            isClient={true}
                            showBuilding={false}
                            showClientOrProvider={true}
                            title='Facturas a clientes'
                            totalAmount={clientTotalPriceSum}
                            paidAmount={clientPaidAmountSum}
                            remainingAmountSum={clientRemainingAmountSum}
                            invoices={clientInvoices}
                            fetch={fetchClientInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={clientInvoicesTotal}
                            currentPage={clientInvoicesPage}
                            onPageChange={setClientInvoicesPage}
                            exportParams={{ building_id: id, only_client_invoices: true }}
                        />
                        <InvoiceRetentionTable
                            isInvoice={false}
                            isClient={true}
                            showBuilding={false}
                            showClientOrProvider={true}
                            title='Retenciones a clientes'
                            totalAmount={clientTotalRetention}
                            paidAmount={clientPaidRetention}
                            remainingAmountSum={clientRemainingRetentionSum}
                            invoices={clientInvoices}
                            fetch={fetchClientInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={clientInvoicesTotal}
                            currentPage={clientInvoicesPage}
                            onPageChange={setClientInvoicesPage}
                        />

                        {/* Provider invoices – paginated */}
                        <InvoiceRetentionTable
                            isInvoice={true}
                            isClient={false}
                            uploadDocument={true}
                            showBuilding={false}
                            showClientOrProvider={true}
                            title='Facturas a proveedores'
                            totalAmount={providerTotalPriceSum}
                            paidAmount={providerPaidAmountSum}
                            remainingAmountSum={providerRemainingAmountSum}
                            invoices={providerInvoices}
                            fetch={fetchProviderInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={providerInvoicesTotal}
                            currentPage={providerInvoicesPage}
                            onPageChange={setProviderInvoicesPage}
                            exportParams={{ building_id: id, only_provider_invoices: true }}
                        />
                        <InvoiceRetentionTable
                            isInvoice={false}
                            uploadDocument={false}
                            isClient={false}
                            showBuilding={false}
                            showClientOrProvider={true}
                            title='Retenciones a proveedores'
                            totalAmount={providerTotalRetention}
                            paidAmount={providerPaidRetention}
                            remainingAmountSum={providerRemainingRetentionSum}
                            invoices={providerInvoices}
                            fetch={fetchProviderInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={providerInvoicesTotal}
                            currentPage={providerInvoicesPage}
                            onPageChange={setProviderInvoicesPage}
                        />

                        {/* Create invoice dialog */}
                        {selectedCert && (
                            <Dialog open={openInvoiceModal} onClose={handleCloseInvoice}>
                                <DialogTitle>Crear Factura</DialogTitle>
                                <DialogContent>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha factura"
                                            value={invoiceDate}
                                            onChange={(newValue) => handleDateChange('invoice_date', newValue)}
                                        />
                                    </Grid>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha vencimiento factura"
                                            value={invoiceLimitDate}
                                            onChange={(newValue) => handleDateChange('invoice_limit_date', newValue)}
                                        />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            name="vat"
                                            label="Iva"
                                            fullWidth
                                            value={vat}
                                            onChange={handleChangeInvoiceModal}
                                            margin="normal"
                                            type="number"
                                            error={!!errors.vat}
                                            helperText={errors.vat} />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            name="retention"
                                            label="Retención (porcentaje)"
                                            fullWidth
                                            value={retention}
                                            onChange={handleChangeInvoiceModal}
                                            margin="normal"
                                            type="number"
                                            error={!!errors.retention}
                                            helperText={errors.retention} />
                                    </Grid>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha vencimiento retención"
                                            value={retentionDate}
                                            onChange={(newValue) => handleDateChange('retention_limit_date', newValue)}
                                        />
                                    </Grid>
                                    <br />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseInvoice} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button color="primary" disabled={!!errors.vat || !!errors.retention || !retention || !retentionDate} onClick={handleCreateInvoice}>
                                        Crear Factura
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        )}

                        {/* Create certification dialog */}
                        <Dialog open={openCertModal} onClose={handleCloseCert}>
                            <DialogTitle>Crear Certificación</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Por favor, rellena los detalles de la nueva certificación.
                                </DialogContentText>
                                <TextField
                                    fullWidth
                                    label="Descripción"
                                    name="description"
                                    value={certificationForm.description}
                                    onChange={handleChangeCertification}
                                    margin="normal"
                                />
                                <br />
                                <br />
                                <DatePickerComponent
                                    label="Fecha"
                                    value={certificationForm.certification_date}
                                    onChange={(newValue) => handleDateChange('certification_date', newValue)}
                                />
                                <br />
                                <br />
                                {items.filter(item => !item.disabled).map((item) => (
                                    <Grid container spacing={2} key={item.id} alignItems="center">
                                        <Grid item xs={6}>
                                            <span>{item.name}</span>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                margin="dense"
                                                label="Porcentaje progreso"
                                                type="number"
                                                inputProps={{ min: 0, max: 100 }}
                                                fullWidth
                                                value={
                                                    certificationForm.items && certificationForm.items.length > 0
                                                        ? (certificationForm.items.find(certItem => certItem.item_id === item.id)?.percentage || '')
                                                        : ''
                                                }
                                                onChange={(e) => handleInputCertificationChange(item.id, e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                ))}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseCert} color="primary">
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={() => handleCreateCertification(certificationForm)}
                                    color="primary"
                                    disabled={!(certificationForm.items && certificationForm.items.some(item => item.percentage > 0))}
                                >
                                    Crear
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Confirm delete dialog */}
                        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                            <DialogTitle>¿Estás seguro?</DialogTitle>
                            <DialogActions>
                                <Button onClick={handleCloseDeleteDialog} color="primary">
                                    Volver
                                </Button>
                                <Button onClick={handleDeleteItem} color="error">
                                    Eliminar
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Add item dialog */}
                        <Dialog open={openItemModal} onClose={handleCloseItemModal} maxWidth="sm" fullWidth>
                            <DialogTitle>Añadir Partida</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Por favor, complete el formulario para añadir una nueva partida.
                                </DialogContentText>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        name="name"
                                        value={itemForm.name || ''}
                                        onChange={handleItemChange}
                                        error={!!itemFormErrors.name}
                                        helperText={itemFormErrors.name}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Descripción"
                                        name="description"
                                        value={itemForm.description || ''}
                                        onChange={handleItemChange}
                                        error={!!itemFormErrors.description}
                                        helperText={itemFormErrors.description}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Precio Unitario"
                                        name="unit_price"
                                        type="number"
                                        value={itemForm.unit_price || ''}
                                        onChange={handleItemChange}
                                        error={!!itemFormErrors.unit_price}
                                        helperText={itemFormErrors.unit_price}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Cantidad"
                                        name="quantity"
                                        type="number"
                                        value={itemForm.quantity || ''}
                                        onChange={handleItemChange}
                                        error={!!itemFormErrors.quantity}
                                        helperText={itemFormErrors.quantity}
                                    />
                                </Box>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <FormControl fullWidth error={!!itemFormErrors.unit}>
                                        <InputLabel id={`unit-label`}>Unidad</InputLabel>
                                        <Select
                                            labelId={`unit-label`}
                                            name="unit"
                                            fullWidth
                                            value={itemForm.unit || ''}
                                            onChange={handleItemChange}
                                            label="Unidad"
                                        >
                                            <MenuItem value="uds">UDS</MenuItem>
                                            <MenuItem value="kg">KG</MenuItem>
                                            <MenuItem value="l">L</MenuItem>
                                            <MenuItem value="hora">HORA</MenuItem>
                                            <MenuItem value="m3">M3</MenuItem>
                                        </Select>
                                        {!!itemFormErrors.unit && (
                                            <FormHelperText>{itemFormErrors.unit}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseItemModal} color="secondary">
                                    Cancelar
                                </Button>
                                <Button onClick={handleItemSubmit} disabled={!itemForm.unit || !itemForm.unit_price || !itemForm.name || !itemForm.quantity} color="primary">
                                    Añadir Partida
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Item info dialog */}
                        <Dialog open={openInfoModal} onClose={handleInfoClose}>
                            <DialogTitle>Información</DialogTitle>
                            <DialogContent>
                                <DialogContentText>Nombre: {infoContent.name}</DialogContentText>
                                <DialogContentText>Descripción: {infoContent.description}</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleInfoClose} color="primary">
                                    Cerrar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </div>
            )}
        </div>
    );
};

export default EditBuilding;
